import React, { useState, useCallback, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { WORKSPACES } from '@/lib/workspace'
import { useTheme } from '@/contexts/ThemeContext'
import { getUnreadCount } from '@/lib/api/notifications'
import NotificationCenter from './NotificationCenter'
import { supabase } from '@/lib/supabase'
import {
  HiHome, HiFolder, HiUsers, HiCube, HiHeart, HiOfficeBuilding,
  HiChartBar, HiCog, HiClipboardList, HiBell, HiMenu, HiX,
  HiSun, HiMoon, HiLogout, HiUserCircle, HiShieldCheck,
  HiInbox, HiCalendar, HiEye, HiSwitchHorizontal, HiAcademicCap, HiTrendingUp,
} from 'react-icons/hi'

interface NavItem {
  label: string
  path: string
  icon: React.ReactNode
  adminOnly?: boolean
  badge?: number
}

interface AppShellProps {
  children:      React.ReactNode
  pendingHours?: number
  newInquiries?: number
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: <HiHome /> },
  { label: 'Work Queue', path: '/work-queue', icon: <HiInbox /> },
  { label: 'Client & Family Services', path: '/workspace/clients', icon: <HiHeart /> },
  { label: 'Agencies & Partnerships', path: '/workspace/partners', icon: <HiOfficeBuilding /> },
  { label: 'Volunteer Services', path: '/workspace/volunteers', icon: <HiUsers /> },
  { label: 'Operations & Administration', path: '/workspace/operations', icon: <HiShieldCheck /> },
  { label: 'Inventory', path: '/inventory', icon: <HiCube />, adminOnly: true },
  { label: 'Volunteers', path: '/volunteers', icon: <HiUsers />, adminOnly: true },
  { label: 'Donors', path: '/donors', icon: <HiHeart />, adminOnly: true },
  { label: 'Partners', path: '/partners', icon: <HiOfficeBuilding />, adminOnly: true },
  { label: 'Staff', path: '/staff', icon: <HiShieldCheck />, adminOnly: true },
  { label: 'Impact & Reporting', path: '/reports', icon: <HiChartBar />, adminOnly: true },
  { label: 'Audit Log', path: '/audit', icon: <HiClipboardList />, adminOnly: true },
  { label: 'Settings', path: '/settings', icon: <HiCog />, adminOnly: true },
]

// NavLink — module scope for stable identity
const NavLink = React.memo(({
  item, active, collapsed, onClick,
}: {
  item: NavItem; active: boolean; collapsed: boolean; onClick?: () => void
}) => (
  <Link
    to={item.path}
    onClick={onClick}
    aria-label={collapsed ? item.label : undefined}
    aria-current={active ? 'page' : undefined}
    title={collapsed ? item.label : undefined}
    className={[
      'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 relative',
      active
        ? 'bg-brand-500 text-white shadow-sm'
        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100',
    ].join(' ')}
  >
    <span className="text-lg flex-shrink-0" aria-hidden="true">{item.icon}</span>
    {!collapsed && <span className="text-sm font-medium truncate flex-1">{item.label}</span>}
    {item.badge !== undefined && item.badge > 0 && (
      <span
        aria-label={`${item.badge} pending`}
        className={[
          'flex-shrink-0 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-xs font-bold',
          collapsed ? 'absolute -top-1 -right-1' : '',
          active ? 'bg-white text-brand-600' : 'bg-brand-500 text-white',
        ].join(' ')}
      >
        {item.badge}
      </span>
    )}
  </Link>
))
NavLink.displayName = 'NavLink'

const AppShell: React.FC<AppShellProps> = ({ children, pendingHours = 0, newInquiries = 0 }) => {
  const { profile, isAdmin, signOut } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const location  = useLocation()
  const navigate  = useNavigate()

  const [collapsed,   setCollapsed]   = useState(false)
  const [mobileOpen,  setMobileOpen]  = useState(false)
  const [notifOpen,   setNotifOpen]   = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [adminMenuOpen, setAdminMenuOpen] = useState(false)

  // AUDIT FIX #8+17: Fetch real notification unread count for bell badge
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notif-unread', profile?.id],
    queryFn:  () => profile ? getUnreadCount(profile.id) : Promise.resolve(0),
    enabled:  !!profile,
    // Keep the bell badge responsive even if Realtime is delayed or unavailable.
    refetchInterval: 10_000,
  })

  const qc = useQueryClient()

  // Live shell badges: public inquiries update the inquiry count, while
  // server-created notifications update the bell count immediately.
  useEffect(() => {
    if (!profile?.id) return
    const channel = supabase
      .channel('shell-live-badges')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'outreach_inquiries' },
        () => { qc.invalidateQueries({ queryKey: ['inquiries', 'stats'] }) }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `recipient_id=eq.${profile.id}` },
        () => {
          qc.invalidateQueries({ queryKey: ['notif-unread', profile.id] })
          qc.invalidateQueries({ queryKey: ['notifications', profile.id] })
        }
      )
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [profile?.id, qc])

  const handleSignOut   = useCallback(async () => { await signOut(); navigate('/login') }, [signOut, navigate])
  const toggleCollapsed = useCallback(() => setCollapsed(c => !c), [])
  const closeMobile     = useCallback(() => setMobileOpen(false), [])

  // Close mobile drawer and profile dropdown on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setMobileOpen(false); setProfileOpen(false); setAdminMenuOpen(false) }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // ── Workspace context (replaces local viewAs state) ────────────
  const {
    workspace, isPreviewing, setPreviewAs,
    previewingAs, availableWorkspaces,
  } = useWorkspace()

  const WORKSPACE_NAV_LABELS = workspace.navItems.map(n => n.label)
  const effectiveIsAdmin     = isAdmin && !isPreviewing

  const toggleNotif   = useCallback(() => { setNotifOpen(o => !o);  setProfileOpen(false); setAdminMenuOpen(false) }, [])
  const toggleAdminMenu = useCallback(() => { setAdminMenuOpen(o => !o); setProfileOpen(false); setNotifOpen(false) }, [])
  const toggleProfile = useCallback(() => { setProfileOpen(o => !o); setNotifOpen(false)  }, [])
  const closeNotif    = useCallback(() => setNotifOpen(false), [])
  const closeProfile  = useCallback(() => setProfileOpen(false), [])

  // Partner and volunteer accounts receive a focused portal navigation,
  // not the staff dashboard navigation. Route guards enforce the same boundary.
  const visibleItems: NavItem[] = !isPreviewing && profile?.role === 'volunteer'
    ? [{ label: 'My Volunteer Portal', path: '/volunteer-portal', icon: <HiUsers /> }]
    : !isPreviewing && profile?.role === 'partner_agency'
      ? [{ label: 'Partner Portal', path: '/partner-portal', icon: <HiOfficeBuilding /> }]
      : NAV_ITEMS
          .filter(item => item.label === 'Dashboard' || WORKSPACE_NAV_LABELS.includes(item.label))
          .filter(item => !item.adminOnly)
          .map(item =>
            item.label === 'Volunteers' ? { ...item, badge: pendingHours } :
            item.label === 'Inquiries'  ? { ...item, badge: newInquiries  } :
            item)

  const adminItems = NAV_ITEMS.filter(item => item.adminOnly && effectiveIsAdmin)

  const SidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-gray-200 dark:border-gray-800 ${collapsed ? 'justify-center' : ''}`}>
        <div className="w-9 h-9 rounded-xl bg-brand-500 flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-sm" aria-hidden="true">W</span>
        </div>
        {!collapsed && (
          <div>
            <p className="text-sm font-bold text-gray-900 dark:text-gray-100 leading-tight">W.E.E.</p>
            <p className="text-xs text-gray-500 leading-tight">Guided Access</p>
          </div>
        )}
      </div>

      {/* Nav */}
      {/* Workspace Switcher — admin preview (Phase 2 seed) */}
      {isAdmin && (
        <div className={`px-3 py-2 border-b border-gray-200 dark:border-gray-800 ${isPreviewing ? 'bg-amber-50 dark:bg-amber-950' : ''}`}>
          {!collapsed ? (
            <div className="space-y-1">
              <p className="text-xs text-gray-400 flex items-center gap-1">
                <HiEye className="text-xs" aria-hidden="true" /> Preview workspace
              </p>
              <select
                value={previewingAs ?? 'admin'}
                onChange={e => setPreviewAs(e.target.value === 'admin' ? null : e.target.value as import('@/lib/workspace').WorkspaceId)}
                aria-label="Preview workspace"
                className="w-full text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                {availableWorkspaces.map(id => (
                  <option key={id} value={id}>{WORKSPACES[id].emoji} {WORKSPACES[id].label}</option>
                ))}
              </select>
              {isPreviewing && (
                <div className="space-y-1">
                  <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                    ⚠ Previewing {workspace.label}
                  </p>
                  {workspace.homeRoute !== '/dashboard' && (
                    <a href={workspace.homeRoute} className="block text-xs text-brand-500 hover:underline">
                      → Open {workspace.label} ↗
                    </a>
                  )}
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => setPreviewAs(isPreviewing ? null : 'staff')}
              aria-label="Toggle workspace preview"
              title="Toggle workspace preview"
              className={`w-full flex justify-center p-1 rounded-lg transition-colors ${isPreviewing ? 'text-amber-500' : 'text-gray-400 hover:text-brand-500'}`}
            >
              <HiSwitchHorizontal aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      <nav aria-label="Main navigation" className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {visibleItems.map(item => (
          <NavLink
            key={item.path}
            item={item}
            active={
              item.label === 'My Schedule'
                ? location.pathname === '/volunteer-portal' && new URLSearchParams(location.search).get('tab') === 'schedule'
                : item.label === 'My Hours'
                  ? location.pathname === '/volunteer-portal' && (new URLSearchParams(location.search).get('tab') === 'hours' || !new URLSearchParams(location.search).get('tab'))
                  : item.label === 'Training'
                    ? location.pathname === '/volunteer-portal' && new URLSearchParams(location.search).get('tab') === 'training'
                    : location.pathname === item.path || location.pathname.startsWith(item.path + '/')
            }
            collapsed={collapsed}
            onClick={closeMobile}
          />
        ))}
      </nav>

      {/* User footer */}
      {profile && !collapsed && (
        <div className="px-3 py-4 border-t border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-3 px-2">
            <div aria-hidden="true" className="w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {profile.first_name?.[0]}{profile.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">{profile.display_name}</p>
              <p className="text-xs text-gray-400 truncate capitalize">{profile.role.replace(/_/g,' ')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex">
      {/* Desktop navigation now lives across the top so the workspace has
          the full page width available for Case Management and dashboards. */}
      <aside aria-label="Desktop sidebar" className="hidden">
        {SidebarContent}
      </aside>

      {/* Mobile Sidebar Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/50 lg:hidden"
              onClick={closeMobile}
              aria-hidden="true"
            />
            <motion.aside
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ duration: 0.2 }}
              aria-label="Mobile navigation"
              className="fixed left-0 top-0 bottom-0 z-50 w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 lg:hidden"
            >
              {SidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Nav */}
        <header className="h-14 flex-shrink-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex items-center px-4 gap-3 z-30">
          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            className="lg:hidden text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-1 rounded"
          >
            <HiMenu className="text-xl" aria-hidden="true" />
          </button>

          {/* Desktop top navigation */}
          <div className="hidden lg:flex items-center gap-1 min-w-0">
            <Link to="/dashboard" className="flex items-center gap-2 mr-3 pr-3 border-r border-gray-200 dark:border-gray-800">
              <span className="w-7 h-7 rounded-lg bg-brand-500 flex items-center justify-center text-white font-bold text-xs">W</span>
              <span className="text-sm font-bold text-gray-900 dark:text-gray-100">W.E.E.</span>
            </Link>
            <nav aria-label="Workspace navigation" className="flex items-center gap-1 overflow-x-auto max-w-[calc(100vw-420px)]">
              {visibleItems.map(item => (
                <NavLink
                  key={item.label + item.path}
                  item={item}
                  active={
                    item.label === 'My Schedule'
                      ? location.pathname === '/volunteer-portal' && new URLSearchParams(location.search).get('tab') === 'schedule'
                      : item.label === 'My Hours'
                        ? location.pathname === '/volunteer-portal' && (new URLSearchParams(location.search).get('tab') === 'hours' || !new URLSearchParams(location.search).get('tab'))
                        : item.label === 'Training'
                          ? location.pathname === '/volunteer-portal' && new URLSearchParams(location.search).get('tab') === 'training'
                          : location.pathname === item.path || location.pathname.startsWith(item.path + '/')
                  }
                  collapsed={false}
                />
              ))}
            </nav>
          </div>

          <div className="flex-1" />

          {effectiveIsAdmin && adminItems.length > 0 && (
            <div className="relative hidden lg:block">
              <button
                onClick={toggleAdminMenu}
                aria-expanded={adminMenuOpen}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                ⚙ Administration <span aria-hidden="true">⌄</span>
              </button>
              <AnimatePresence>
                {adminMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                    className="absolute right-0 top-full mt-2 w-52 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-xl z-50 p-2"
                  >
                    {adminItems.map(item => (
                      <Link
                        key={item.label}
                        to={item.path}
                        onClick={() => setAdminMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <span className="text-base">{item.icon}</span>{item.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Public site — intentionally opens separately so staff can keep
              the dashboard operational while reviewing the public experience. */}
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open public W.E.E. site in a new tab"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <HiEye className="text-base" aria-hidden="true" />
            Public Site ↗
          </a>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1.5 rounded-lg"
          >
            {theme === 'dark'
              ? <HiSun className="text-lg" aria-hidden="true" />
              : <HiMoon className="text-lg" aria-hidden="true" />
            }
          </button>

          <div className="relative">
            <button
              onClick={toggleNotif}
              aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
              aria-expanded={notifOpen}
              className="relative text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1.5 rounded-lg transition-colors"
            >
              <HiBell className="text-xl" aria-hidden="true" />
              {unreadCount > 0 && (
                <span
                  aria-hidden="true"
                  className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-brand-500 rounded-full text-white text-xs flex items-center justify-center font-bold"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            <NotificationCenter open={notifOpen} onClose={closeNotif} />
          </div>

          {/* Profile dropdown */}
          <div className="relative">
            <button
              onClick={toggleProfile}
              aria-label="User menu"
              aria-expanded={profileOpen}
              className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <div aria-hidden="true" className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-bold">
                {profile?.first_name?.[0]}{profile?.last_name?.[0]}
              </div>
            </button>

            <AnimatePresence>
              {profileOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={closeProfile} aria-hidden="true" />
                  <motion.div
                    role="menu"
                    aria-label="User options"
                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.96 }}
                    transition={{ duration: 0.12 }}
                    className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl z-50"
                  >
                    <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{profile?.display_name}</p>
                      <p className="text-xs text-gray-400 truncate">{profile?.email}</p>
                    </div>
                    <div className="py-1" role="none">
                      <Link
                        to="/profile"
                        onClick={closeProfile}
                        role="menuitem"
                        className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <HiUserCircle className="text-gray-400" aria-hidden="true" /> My Profile
                      </Link>
                      {(profile?.role === 'volunteer' || profile?.role === 'volunteer_coordinator') && (
                        <Link
                          to="/volunteer-portal"
                          onClick={closeProfile}
                          role="menuitem"
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <HiUsers className="text-brand-500" aria-hidden="true" /> Volunteer Workspace
                        </Link>
                      )}
                      {profile?.role === 'partner_agency' && (
                        <Link
                          to="/partner-portal"
                          onClick={closeProfile}
                          role="menuitem"
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <HiOfficeBuilding className="text-brand-500" aria-hidden="true" /> Partner Workspace
                        </Link>
                      )}
                      {isAdmin && (
                        <Link
                          to="/settings"
                          onClick={closeProfile}
                          role="menuitem"
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <HiShieldCheck className="text-brand-500" aria-hidden="true" /> Settings
                        </Link>
                      )}
                      <button
                        onClick={handleSignOut}
                        role="menuitem"
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                      >
                        <HiLogout aria-hidden="true" /> Sign Out
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  )
}

export default AppShell
